import h from 'hyperscript';

import { AlertDialog } from 'ui/dialog/alert/alert-dialog';

import { confirm } from 'cadenza/alert';
import { isObjecttypeAvailableAndUnbound } from 'cadenza/api-client/objecttypes-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import type { AttributeName } from 'cadenza/model/attribute-name';
import { type ObjecttypeIdString, objecttypeIdToString } from 'cadenza/repository/model/objecttype-id';
import type { ObjecttypeName } from 'cadenza/repository/model/objecttype-name';
import { getStore } from 'cadenza/store/cadenza-store';
import { assertUnreachable } from 'cadenza/utils/custom-error';
import type { KeysWithType } from 'cadenza/workbook/enrichment/enrichment-utils';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { createAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import { executeCommand, getAttributeById, getObjecttypeById } from 'cadenza/workbook/workbook.state';

import i18n from './rerun-enrichment.properties';

export async function verifyAllResourcesForRerunAvailable(enrichmentDto: EnrichmentDto) {
  const {
    // could be for example:
    // - an objecttype necessary to rerun the enrichment isn't added to the workbook and the user doesn't have the privileges to add it
    // - an attribute necessary to rerun the enrichment has been removed in the meantime
    // if this list is not empty, the enrichment cannot be executed.
    unavailableResources,
    // objecttypes that are required for the rerun, isn't added to the workbook, but the user can add it
    missingButAvailableObjecttypes,
  } = await executeResourceVerification(enrichmentDto);

  if (missingButAvailableObjecttypes.length) {
    const missingObjecttypesAdded =
      missingButAvailableObjecttypes.length === 1 ?
        await addMissingObjecttype(
          missingButAvailableObjecttypes[0].resourceType,
          missingButAvailableObjecttypes.map((res) => res.objecttypeId),
        )
      : await addMissingObjecttype(
          'both',
          missingButAvailableObjecttypes.map((res) => res.objecttypeId),
        );
    if (missingObjecttypesAdded) {
      // missing objecttypes have been added - execute the resource verification again
      return verifyAllResourcesForRerunAvailable(enrichmentDto);
    }
    // user hasn't added the missing objecttypes
    return false;
  } else if (unavailableResources.length === 0) {
    // no missing objecttypes, no missing resources. Everything is fine
    return true;
  } else {
    // all missing but available objecttypes have been added and there are still missing resources
    await showRerunEnrichmentNotPossibleDialog(...unavailableResources);
    return false;
  }
}

async function executeResourceVerification(enrichmentDto: EnrichmentDto) {
  switch (enrichmentDto.enrichmentType) {
    case 'StructuredGeocodingEnrichment':
    case 'UnstructuredGeocodingEnrichment':
    case 'IpGeocodingEnrichment':
    case 'ReverseGeocodingEnrichment':
    case 'AnalyticsExtensionEnrichment':
    case 'IsochronesEnrichment':
      return verifyObjecttype('enriched', enrichmentDto.enrichedObjecttypeId);
    case 'MeasuresFromSpatialIntersectionEnrichment': {
      return Promise.all([
        verifyAttribute(enrichmentDto, 'enriched', 'enrichedObjecttypeName', 'enrichedGeometryAttribute'),
        verifyAttribute(
          enrichmentDto,
          'provider',
          'measureProvidingObjecttypeName',
          'measureProvidingGeometryAttribute',
        ),
      ]).then(([enriched, provider]) => ({
        unavailableResources: [...enriched.unavailableResources, ...provider.unavailableResources],
        missingButAvailableObjecttypes: [
          ...provider.missingButAvailableObjecttypes,
          ...provider.missingButAvailableObjecttypes,
        ],
      }));
    }
    default:
      assertUnreachable(enrichmentDto);
  }
}

async function verifyAttribute<D extends EnrichmentDto>(
  enrichmentDto: D,
  type: 'enriched' | 'provider',
  objecttypeNameKey: KeysWithType<D['parametrization'], ObjecttypeName>,
  enrichedAttributeKey: KeysWithType<D['parametrization'], AttributeName>,
) {
  const parametrization = enrichmentDto.parametrization as D['parametrization'];
  const objecttypeName = parametrization[objecttypeNameKey] as ObjecttypeName;
  const objecttypeId = objecttypeIdToString(enrichmentDto.repositoryName, objecttypeName);

  const { missingButAvailableObjecttypes, unavailableResources } = await verifyObjecttype(type, objecttypeId);
  if (missingButAvailableObjecttypes.length === 0 && unavailableResources.length === 0) {
    // objecttype is here, let's check the attribute
    const attributeName = parametrization[enrichedAttributeKey] as AttributeName;
    const attributeId = createAttributeId(objecttypeId, attributeName);
    const attributeAvailable = Boolean(getAttributeById(getStore().getState(), attributeId));
    if (!attributeAvailable) {
      unavailableResources.push(`${type}-attribute-unavailable`);
    }
  }
  return { unavailableResources, missingButAvailableObjecttypes };
}

async function verifyObjecttype(type: 'enriched' | 'provider', objecttypeId: ObjecttypeIdString) {
  const objecttypeAvailable = Boolean(getObjecttypeById(getStore().getState(), objecttypeId));
  const unavailableResources: RerunNotPossibleReason[] = [];
  const missingButAvailableObjecttypes: MissingObjecttype[] = [];
  if (!objecttypeAvailable) {
    // objecttype is not added to the workbook. Make a quick HEAD request to see if the objecttype is still accessible.
    const missingButAvailable = await isObjecttypeAvailableAndUnbound(objecttypeId);
    if (missingButAvailable) {
      // the objecttype is missing, but can be added to the workbook.
      missingButAvailableObjecttypes.push({ resourceType: 'enriched', objecttypeId });
    } else {
      unavailableResources.push(`${type}-objecttype-unavailable`);
      unavailableResources.push(`${type}-attribute-unavailable`);
    }
  }
  return { unavailableResources, missingButAvailableObjecttypes };
}

interface MissingObjecttype {
  objecttypeId: ObjecttypeIdString;
  resourceType: 'enriched' | 'provider';
}

async function addMissingObjecttype(
  type: 'both' | 'enriched' | 'provider',
  objecttypeIds: ObjecttypeIdString[],
): Promise<boolean> {
  const confirmed = await confirm(
    i18n(`addMissingObjecttype.dialog.<${type}>.title`),
    i18n('addMissingObjecttype.message', { count: objecttypeIds.length }),
    {
      type: 'warning',
      submit: i18n('addMissingObjecttype.submit'),
    },
  );
  if (confirmed) {
    await getStore().dispatch(executeCommand(Command.ADD_OBJECTTYPES({ objecttypeIds })));
  }
  return !!confirmed;
}

type RerunNotPossibleReason =
  | 'enriched-objecttype-unavailable'
  | 'enriched-attribute-unavailable'
  | 'provider-objecttype-unavailable'
  | 'provider-attribute-unavailable';

export function showRerunEnrichmentNotPossibleDialog(...reasons: RerunNotPossibleReason[]) {
  const dialog = new AlertDialog(i18n('verification.dialog.title'), {
    type: 'error',
    message: h('.d-alert.d-alert-error', [
      h('form-group', [
        h('label', i18n('verification.dialog.message')),
        h(
          'ul',
          reasons.map((reason) => h('li', i18n(`verification.reason.<${reason}>`))),
        ),
      ]),
    ]) as HTMLElement,
  });
  return dialog.connectAndShow();
}
